import { useEffect, useState } from 'react';
import { Button, Input, Select, SelectItem, Textarea } from '@nextui-org/react';
import { useTranslation } from 'common/src/hooks/useTranslation';
import type {
  CaseAction,
  CaseCommandResult,
  CaseInput,
  CaseOptions,
  CaseOwner,
  CaseScope,
  ShipmentCase,
} from 'common/src/api/shipment-cases';
import { SINISTER_RESPONSIBLE_OPTIONS } from 'common/src/utils/sinister';
import { Alert } from '../../components/ui';
import { caseKinds, methods, outcomes, sources } from './model';
import { useCaseCommand } from './use-case-command';
import CaseExternalEntities from './CaseExternalEntities';

export function OwnerSelect({
  value,
  onChange,
  people,
  disabled,
  label = 'Encargado',
}: {
  value: string;
  onChange: (v: string) => void;
  people: CaseOptions['people'];
  disabled?: boolean;
  label?: string;
}) {
  const { t } = useTranslation('shipmentCases');
  return (
    <Select
      label={t(label)}
      selectedKeys={value ? [value] : []}
      onSelectionChange={(keys) => onChange(String(Array.from(keys)[0] || ''))}
      isDisabled={disabled}
    >
      {people
        .map((p) => ({ key: `user:${p.id}`, label: p.name }))
        .map((o) => (
          <SelectItem key={o.key} textValue={o.label}>
            {o.label}
          </SelectItem>
        ))}
    </Select>
  );
}
const ownerValue = (owner: CaseOwner) =>
  owner.kind === 'user' ? `user:${owner.id}` : '';
// También lo usa NewCaseForm.
const ownerBody = (value: string): CaseOwner => ({
  kind: 'user',
  id: Number(value.slice(5)),
});

export default function CaseForm({
  scope,
  identity,
  action,
  record,
  options,
  onSaved,
  onCancel,
  pendingInputs = [],
  onLockedChange,
  selectedInputId,
}: {
  scope: CaseScope;
  identity: string;
  action: CaseAction['action'];
  record?: ShipmentCase;
  options: CaseOptions;
  onSaved: (r: CaseCommandResult) => void;
  onCancel: () => void;
  pendingInputs?: CaseInput[];
  onLockedChange: (locked: boolean) => void;
  selectedInputId?: number;
}) {
  const { t } = useTranslation('shipmentCases');
  const [note, setNote] = useState('');
  const [responsible, setResponsible] = useState(record?.responsible || '');
  const [responsibleName, setResponsibleName] = useState(
    record?.responsible_name || ''
  );
  const [days, setDays] = useState('7');
  const [recipientKind, setRecipientKind] = useState('internal');
  const [externalEntity, setExternalEntity] = useState<number | null>(null);
  const [entityReady, setEntityReady] = useState(false);
  const previousFollowup = pendingInputs.find(
    (i) => i.id === selectedInputId
  )?.followup_owner;
  const [followupOwner, setFollowupOwner] = useState(
    previousFollowup?.kind === 'user' &&
      options.people.some((p) => p.id === previousFollowup.id)
      ? ownerValue(previousFollowup)
      : ''
  );
  const [recipientOwner, setRecipientOwner] = useState('');
  const [kind, setKind] = useState(record?.kind || 'observation');
  const [outcome, setOutcome] = useState('resolved');
  const [benchmark, setBenchmark] = useState('');
  const [obtained, setObtained] = useState('');
  const [benchmarkSource, setBenchmarkSource] = useState('reported');
  const [obtainedSource, setObtainedSource] = useState('reported');
  const [method, setMethod] = useState('own_vessel');
  const [inputId, setInputId] = useState(
    selectedInputId
      ? String(selectedInputId)
      : pendingInputs.length === 1
        ? String(pendingInputs[0].id)
        : ''
  );
  const [files, setFiles] = useState<File[]>([]);
  const command = useCaseCommand(scope, identity, onSaved);
  const frozen = command.frozen || command.busy;
  useEffect(() => {
    onLockedChange(frozen);
    return () => onLockedChange(false);
  }, [frozen, onLockedChange]);
  const body: CaseAction = {
    action,
    case_id: record?.id,
    version: record?.version,
    note,
  };
  if (action === 'responsible')
    Object.assign(body, { responsible, responsible_name: responsibleName });
  if (action === 'escalate') body.kind = kind;
  if (action === 'request_input')
    Object.assign(body, {
      recipient_kind: recipientKind,
      recipient:
        recipientKind === 'internal'
          ? options.people.find((p) => p.id === Number(recipientOwner.slice(5)))
              ?.name || ''
          : undefined,
      recipient_owner:
        recipientKind === 'internal' ? ownerBody(recipientOwner) : undefined,
      external_entity_id:
        recipientKind === 'external' ? externalEntity : undefined,
      followup_owner:
        recipientKind === 'external' ? ownerBody(followupOwner) : undefined,
      due_days: Number(days),
    });
  if (['receive_input', 'assign_input'].includes(action))
    body.input_id = Number(inputId);
  if (action === 'assign_input') body.followup_owner = ownerBody(followupOwner);
  if (action === 'close') body.outcome = outcome as CaseAction['outcome'];
  if (action === 'value')
    Object.assign(body, {
      benchmark_usd_kg: Number(benchmark),
      obtained_usd_kg: Number(obtained),
      benchmark_source: benchmarkSource,
      obtained_source: obtainedSource,
      methodology: method,
    });
  const validFollowup = options.people.some(
    (p) => followupOwner === `user:${p.id}`
  );
  const fixedInput = pendingInputs.find((i) => i.id === selectedInputId);
  const valid =
    action === 'assign'
      ? false
      : action === 'request_input'
        ? Boolean(
            (recipientKind === 'internal'
              ? options.people.some((p) => recipientOwner === `user:${p.id}`)
              : externalEntity && entityReady && validFollowup) &&
              [3, 7, 14].includes(Number(days)) &&
              note.trim()
          )
        : action === 'assign_input'
          ? Boolean(
              pendingInputs.some(
                (i) =>
                  String(i.id) === inputId && i.recipient_kind === 'external'
              ) && validFollowup
            )
          : action === 'receive_input'
            ? Boolean(
                pendingInputs.some((i) => String(i.id) === inputId) &&
                  note.trim()
              )
            : action === 'escalate'
              ? Object.prototype.hasOwnProperty.call(caseKinds, kind) &&
                kind !== record?.kind
              : action === 'value'
                ? Boolean(
                    benchmark !== '' &&
                      obtained !== '' &&
                      Number(benchmark) >= 0 &&
                      Number(obtained) >= 0 &&
                      note.trim()
                  )
                : ['close', 'reopen'].includes(action)
                  ? Boolean(note.trim())
                  : action === 'note'
                    ? Boolean(note.trim() || files.length)
                    : true;
  const pick = (
    label: string,
    value: string,
    set: (v: string) => void,
    choices: Record<string, string>
  ) => (
    <Select
      label={t(label)}
      selectedKeys={value ? [value] : []}
      onSelectionChange={(keys) => set(String(Array.from(keys)[0] || ''))}
      isDisabled={frozen}
    >
      {Object.entries(choices).map(([key, text]) => (
        <SelectItem key={key} textValue={t(text)}>
          {t(text)}
        </SelectItem>
      ))}
    </Select>
  );
  return (
    <form
      className='flex flex-col gap-4'
      onSubmit={(e) => {
        e.preventDefault();
        if (valid || frozen) void command.save(body, files);
      }}
    >
      {action === 'responsible' && (
        <>
          {pick('Responsable de la pérdida', responsible, setResponsible, {
            '': 'Sin asignar',
            ...Object.fromEntries(
              SINISTER_RESPONSIBLE_OPTIONS.map((v) => [v, v])
            ),
          })}
          <Input
            label={t('Responsable específico (opcional)')}
            value={responsibleName}
            onValueChange={setResponsibleName}
            isDisabled={frozen}
          />
        </>
      )}
      {action === 'escalate' && (
        <>
          {pick(
            'Tipo',
            kind,
            (value) => setKind(value as typeof kind),
            caseKinds
          )}
          <p className='text-sm text-default-500'>
            {t(
              'Puedes elegir cualquier tipo. Se conserva el número y el historial del caso.'
            )}
          </p>
        </>
      )}
      {action === 'request_input' && (
        <>
          {pick('Destinatario', recipientKind, setRecipientKind, {
            internal: 'Interno',
            external: 'Externo',
          })}
          {recipientKind === 'internal' ? (
            <OwnerSelect
              value={recipientOwner}
              onChange={setRecipientOwner}
              people={options.people}
              disabled={frozen}
              label='Destinatario interno'
            />
          ) : (
            <CaseExternalEntities
              exporterId={scope.exporter_id}
              identity={identity}
              selected={externalEntity}
              onSelectionChange={setExternalEntity}
              onReadyChange={setEntityReady}
              disabled={frozen}
            />
          )}
          {recipientKind === 'external' && (
            <>
              <OwnerSelect
                value={followupOwner}
                onChange={setFollowupOwner}
                people={options.people}
                disabled={frozen}
                label='Encargado de seguimiento'
              />
              <p className='text-sm text-default-500'>
                {t(
                  'Usuario interno responsable de coordinar y dar seguimiento a esta solicitud.'
                )}
              </p>
            </>
          )}
          {pick('Plazo de respuesta', days, setDays, {
            '3': '3 días',
            '7': '7 días',
            '14': '14 días',
          })}
          {recipientKind === 'external' && (
            <Alert variant='default'>
              {t(
                'Registra una solicitud coordinada por tu equipo. La entidad externa no recibe correo; el encargado de seguimiento recibe un aviso.'
              )}
            </Alert>
          )}
        </>
      )}
      {action === 'assign_input' && (
        <OwnerSelect
          value={followupOwner}
          onChange={setFollowupOwner}
          people={options.people}
          disabled={frozen}
          label='Encargado de seguimiento'
        />
      )}
      {['receive_input', 'assign_input'].includes(action) &&
        (fixedInput ? (
          <p className='text-sm text-default-500'>
            #{fixedInput.id} · {fixedInput.recipient} · {fixedInput.requested}
          </p>
        ) : (
          <Select
            label={t('Solicitud pendiente')}
            selectedKeys={inputId ? [inputId] : []}
            onSelectionChange={(keys) =>
              setInputId(String(Array.from(keys)[0] || ''))
            }
            isDisabled={frozen}
            renderValue={(items) =>
              items.map((item) => item.textValue).join(', ')
            }
          >
            {pendingInputs
              .filter(
                (i) =>
                  action !== 'assign_input' || i.recipient_kind === 'external'
              )
              .map((i) => (
                <SelectItem
                  key={String(i.id)}
                  textValue={`#${i.id} · ${i.recipient} · ${i.requested}`}
                >
                  #{i.id} · {i.recipient} · {i.requested}
                </SelectItem>
              ))}
          </Select>
        ))}
      {action === 'value' && (
        <>
          <Alert variant='default'>
            {t(
              'Ingresa precios comparables en USD/kg y documenta su origen, fecha y conversión. La confianza se calcula; no se generan vínculos financieros.'
            )}
          </Alert>
          {pick('Metodología', method, setMethod, methods)}
          {pick('Fuente del benchmark', benchmarkSource, setBenchmarkSource, {
            official: sources.official,
            reported: sources.reported,
            market: sources.market,
          })}
          <Input
            label={t('Benchmark (USD/kg)')}
            type='number'
            min={0}
            step='0.0001'
            value={benchmark}
            onValueChange={setBenchmark}
            isDisabled={frozen}
          />
          {pick(
            'Fuente del precio obtenido',
            obtainedSource,
            (v) => {
              setObtainedSource(v);
              if (['destroyed', 'unknown'].includes(v)) setObtained('0');
            },
            sources
          )}
          <Input
            label={t('Precio obtenido (USD/kg)')}
            type='number'
            min={0}
            step='0.0001'
            value={obtained}
            onValueChange={setObtained}
            isDisabled={
              frozen || ['destroyed', 'unknown'].includes(obtainedSource)
            }
          />
          {obtainedSource === 'unknown' && (
            <Alert variant='warning'>
              {t(
                'Sin precio obtenido, cero es un supuesto referencial; no acredita destrucción ni una pérdida confirmada.'
              )}
            </Alert>
          )}
          <p className='font-semibold'>
            {t('Pérdida estimada')}: USD{' '}
            {(record?.items.reduce((n, i) => n + Number(i.kilos), 0) || 0) *
              Math.max(0, Number(benchmark) - Number(obtained))}
          </p>
        </>
      )}
      {action === 'close' && (
        <>
          {pick(
            'Resultado',
            outcome,
            setOutcome,
            record?.kind === 'observation'
              ? { resolved: outcomes.resolved, discarded: outcomes.discarded }
              : { resolved: outcomes.resolved, rejected: outcomes.rejected }
          )}
          <Alert variant='default'>
            {t(
              'El cierre conserva la valorización y documenta el resultado. No emite notas de crédito ni modifica liquidaciones.'
            )}
          </Alert>
        </>
      )}
      <Textarea
        label={t(
          action === 'value'
            ? 'Respaldo del cálculo'
            : action === 'request_input'
              ? 'Qué información necesitas'
              : action === 'receive_input'
                ? 'Resumen de la respuesta'
                : action === 'close'
                  ? 'Resolución del caso'
                  : action === 'reopen'
                    ? 'Motivo de reapertura'
                    : 'Comentario'
        )}
        value={note}
        onValueChange={setNote}
        maxLength={8000}
        isDisabled={frozen}
      />
      <label className='flex flex-col gap-2 text-sm'>
        <span>{t('Documentos (máximo 10, hasta 10 MB cada uno)')}</span>
        <input
          type='file'
          multiple
          accept='.pdf,.png,.jpg,.jpeg,.webp,.txt,.eml,.doc,.docx,.xls,.xlsx'
          disabled={frozen}
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
        />
      </label>
      {files.map((f, i) => (
        <p key={i} className='text-xs text-default-500'>
          {f.name} · {Math.ceil(f.size / 1024)} KB
        </p>
      ))}
      {command.error && (
        <Alert variant='danger' showIcon>
          {command.error}
        </Alert>
      )}
      {frozen && !command.busy && (
        <Alert variant='warning'>
          {t(
            'El envío está fijado para reintentar sin duplicar la acción. Conserva esta ventana abierta hasta completarlo.'
          )}
        </Alert>
      )}
      <div className='flex items-center justify-between gap-3'>
        <Button variant='light' onPress={onCancel} isDisabled={frozen}>
          {t('Cancelar')}
        </Button>
        <Button
          color='primary'
          type='submit'
          isLoading={command.busy}
          isDisabled={!valid && !frozen}
        >
          {t(
            frozen
              ? 'Reintentar envío'
              : action === 'close'
                ? 'Dar por cerrado el caso'
                : 'Confirmar'
          )}
        </Button>
      </div>
    </form>
  );
}
